import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import {
  ADVANCED_FILTERS_KEY_PARAM,
  CATEGORY_KEY_PARAM,
  DESC,
  DIRECTION_KEY_PARAM,
  ENDPOINT_NODES,
  FILTERS_KEY_PARAM,
  ORDER_KEY_PARAM,
  PAGE_KEY_PARAM,
  PAGE_PRODUCTS_CATEGORY,
  PAGE_PRODUCTS_DISCOUNT,
  PAGE_PRODUCTS_OFFERS,
  PAGE_PRODUCTS_SEARCH,
  PAGE_PRODUCTS_SUPPLIER,
  PRODUCTS_API_NODE_ROOT_CATEGORY,
  PRODUCTS_API_NODE_ROOT_DISCOUNT,
  PRODUCTS_API_NODE_ROOT_DISCOUNT_EBAT,
  PRODUCTS_API_NODE_ROOT_OFFERS,
  PRODUCTS_API_NODE_ROOT_SEARCH,
  PRODUCTS_API_NODE_ROOT_SUPPLIER,
  PRODUCTS_LOCAL_SORT_CUSTOMER_PRICE,
  PRODUCTS_LOCAL_SORT_PUBLIC_PRICE,
  PRODUCTS_LOCAL_SORT_STOCK,
  SCORE_KEY_PARAM,
  TERM_KEY_PARAM,
} from '@/common/constants';
import useDiscountClasses from '@/common/hooks/discount/useDiscountClasses';
import {
  pagesProductsMapper,
  pagesProductsMutator,
} from '@/common/hooks/prices-and-stocks/dataMappersAndMutators';
import usePricesAndStocks from '@/common/hooks/prices-and-stocks/usePricesAndStocks';
import useLocalSorts from '@/common/hooks/products/useLocalSorts';
import useProducts from '@/common/hooks/products/useProducts';
import usePageParams from '@/common/hooks/usePageParams';

import { useConfig } from '../ConfigContext';
import {
  ProductsContextProviderDefaultProps,
  ProductsContextProviderPropTypes,
} from './ProductsContextProvider.proptypes';

/**
 * Data parameters
 * name: url query and state name
 * defaultValue: default value on state initialization
 * encode: state to url query
 * decode : url query to state
 */
const paramsConfig = {
  [CATEGORY_KEY_PARAM]: {
    name: CATEGORY_KEY_PARAM,
    defaultValue: null,
    encode: (value) => (value !== null ? `${value}` : null),
    decode: (value) => (value !== null ? parseInt(value, 10) : null),
  },
  [TERM_KEY_PARAM]: {
    name: TERM_KEY_PARAM,
    defaultValue: null,
    encode: (value) => value,
    decode: (value) => value,
  },
  [ORDER_KEY_PARAM]: {
    name: ORDER_KEY_PARAM,
    defaultValue: null,
    encode: (value) => value,
    decode: (value) => value,
  },
  [DIRECTION_KEY_PARAM]: {
    name: DIRECTION_KEY_PARAM,
    defaultValue: null,
    encode: (value) => value,
    decode: (value) => value,
  },
  [FILTERS_KEY_PARAM]: {
    name: FILTERS_KEY_PARAM,
    defaultValue: null,
    encode: (value) => (value !== null ? JSON.stringify(value) : null),
    decode: (value) => JSON.parse(value),
  },
  [ADVANCED_FILTERS_KEY_PARAM]: {
    name: ADVANCED_FILTERS_KEY_PARAM,
    defaultValue: null,
    encode: (value) => (value !== null ? JSON.stringify(value) : null),
    decode: (value) => JSON.parse(value),
  },
  [PAGE_KEY_PARAM]: {
    name: PAGE_KEY_PARAM,
    defaultValue: 1,
    encode: (value) => (value !== null ? `${value}` : null),
    decode: (value) => (value !== null ? parseInt(value, 10) : null),
  },
};

/**
 * Get api endpoint from page name and url params
 * @param {string|null} pageName
 * @param {Object<string, string>} URLParams
 * @return {string|null}
 */
const getApiNodeEndpoint = (pageName, URLParams, isMateriaux) => {
  if (!pageName) {
    return null;
  }
  let apiNodeRoot = PRODUCTS_API_NODE_ROOT_CATEGORY;
  if (pageName === PAGE_PRODUCTS_SEARCH) {
    apiNodeRoot = PRODUCTS_API_NODE_ROOT_SEARCH;
  }
  if (pageName === PAGE_PRODUCTS_OFFERS) {
    apiNodeRoot = PRODUCTS_API_NODE_ROOT_OFFERS;
  }
  if (pageName === PAGE_PRODUCTS_SUPPLIER) {
    apiNodeRoot = PRODUCTS_API_NODE_ROOT_SUPPLIER;
  }
  if (pageName === PAGE_PRODUCTS_DISCOUNT) {
    if (isMateriaux) {
      apiNodeRoot = PRODUCTS_API_NODE_ROOT_DISCOUNT;
      return `${ENDPOINT_NODES}/${apiNodeRoot}`;
    }
    apiNodeRoot = PRODUCTS_API_NODE_ROOT_DISCOUNT_EBAT;
  }

  return Object.keys(URLParams).reduce((accu, pathParam) => {
    if (pathParam === 'job') {
      return accu;
    }
    return `${accu}/${URLParams[pathParam]}`;
  }, `${ENDPOINT_NODES}/${apiNodeRoot}`);
};

/**
 * Fix wrong parameters associations
 * Call on every parameter changed and on initialisation from url
 * @param {Object.<string, *>} nextParams
 * @param {Object.<string, *>} prevParams
 * @return {Object.<string, *>}
 */
const consistencyEncoder = (nextParams, prevParams = undefined) => {
  const params = { ...nextParams };
  if (nextParams[TERM_KEY_PARAM]) {
    params[ORDER_KEY_PARAM] = SCORE_KEY_PARAM;
    params[DIRECTION_KEY_PARAM] = DESC;
    if (
      prevParams &&
      prevParams[TERM_KEY_PARAM] !== nextParams[TERM_KEY_PARAM]
    ) {
      params[CATEGORY_KEY_PARAM] = null;
      params[FILTERS_KEY_PARAM] = null;
      params[ADVANCED_FILTERS_KEY_PARAM] = null;
    }
  }
  if (
    prevParams &&
    paramsConfig[CATEGORY_KEY_PARAM].encode(prevParams[CATEGORY_KEY_PARAM]) !==
      paramsConfig[CATEGORY_KEY_PARAM].encode(nextParams[CATEGORY_KEY_PARAM])
  ) {
    params[ADVANCED_FILTERS_KEY_PARAM] = null;
  }

  if (
    prevParams &&
    (paramsConfig[CATEGORY_KEY_PARAM].encode(prevParams[CATEGORY_KEY_PARAM]) !==
      paramsConfig[CATEGORY_KEY_PARAM].encode(nextParams[CATEGORY_KEY_PARAM]) ||
      paramsConfig[FILTERS_KEY_PARAM].encode(prevParams[FILTERS_KEY_PARAM]) !==
        paramsConfig[FILTERS_KEY_PARAM].encode(nextParams[FILTERS_KEY_PARAM]) ||
      paramsConfig[ADVANCED_FILTERS_KEY_PARAM].encode(
        prevParams[ADVANCED_FILTERS_KEY_PARAM],
      ) !==
        paramsConfig[ADVANCED_FILTERS_KEY_PARAM].encode(
          nextParams[ADVANCED_FILTERS_KEY_PARAM],
        ))
  ) {
    params[PAGE_KEY_PARAM] = 1;
  }

  return params;
};

/**
 * Allows modifications on parameters sent to the API
 * @param {Object.<string, *>} params
 * @return {Object.<string, *>}
 */
const apiParamsEncoder = (params) => {
  const apiParams = { ...params };

  /**
   * Api don't use ADVANCED_FILTERS_KEY_PARAM so we merge all filters in FILTERS_KEY_PARAM
   */
  if (params[ADVANCED_FILTERS_KEY_PARAM]) {
    if (!params[FILTERS_KEY_PARAM]) {
      apiParams[FILTERS_KEY_PARAM] = {};
    }
    apiParams[FILTERS_KEY_PARAM] = {
      ...apiParams[FILTERS_KEY_PARAM],
      ...params[ADVANCED_FILTERS_KEY_PARAM],
    };
    delete apiParams[ADVANCED_FILTERS_KEY_PARAM];
  }

  /**
   * We don't send local sort to api
   */
  if (
    params[ORDER_KEY_PARAM] &&
    (params[ORDER_KEY_PARAM] === PRODUCTS_LOCAL_SORT_PUBLIC_PRICE ||
      params[ORDER_KEY_PARAM] === PRODUCTS_LOCAL_SORT_STOCK ||
      params[ORDER_KEY_PARAM] === PRODUCTS_LOCAL_SORT_CUSTOMER_PRICE)
  ) {
    delete apiParams[ORDER_KEY_PARAM];
    delete apiParams[DIRECTION_KEY_PARAM];
  }

  delete apiParams[PAGE_KEY_PARAM];

  return apiParams;
};

const getIsAllProductsLoaded = (data) => {
  if (!data?.pages) return false;

  let totalProducts = 0;
  const productsLoadedCount = data.pages.reduce((count, page, i) => {
    const productCount = page?.products?.data.length ?? 0;
    if (i === data.pages.length - 1) {
      totalProducts = page?.products?.meta.total_items;
    }

    return count + productCount;
  }, 0);

  return totalProducts > 0 && totalProducts === productsLoadedCount;
};

const applyLocalSorts = (formattedData, localSorts, params) => {
  if (!formattedData || !localSorts || localSorts.length === 0) {
    return formattedData;
  }
  /**
   * First, we get if one local sort is active and the sort method to apply
   */
  let localSorter = (d) => d;
  let isLocalSortActive = false;
  const localSortsData = localSorts.map((localSort) => {
    let active = false;
    let direction = null;
    if (params[ORDER_KEY_PARAM] === localSort.code && !localSort.disabled) {
      isLocalSortActive = true;
      active = true;
      direction = params[DIRECTION_KEY_PARAM];
      localSorter = (d) =>
        localSort.sorter(d, localSort.propertiesPath, direction);
    }

    return {
      ...localSort,
      active,
      direction,
    };
  });

  /**
   * Then, we add local sorts in data from api and inactive them if one local sorts is active
   */
  const sortsData = [
    ...formattedData.sorts.data.map((sortDatum) => {
      if (isLocalSortActive) {
        return { ...sortDatum, active: false };
      }
      return sortDatum;
    }),
    ...localSortsData,
  ];

  /**
   * Finally, we sort products
   */
  const sortedProductsData = localSorter(formattedData.products.data);

  return {
    ...formattedData,
    products: {
      ...formattedData.products,
      data: sortedProductsData,
    },
    sorts: {
      ...formattedData.sorts,
      data: sortsData,
    },
  };
};

const categoriesFormatter = (
  categories,
  slug,
  parentPath,
  findInAncestors = false,
) => {
  let formattedCategories = [];
  let find = findInAncestors;

  categories.forEach((category) => {
    if (category.slug === slug && category.children.length > 0) {
      find = true;
    }
    const formattedCategory = {
      ...category,
      path: findInAncestors ? `${parentPath}/${category.slug}` : parentPath,
    };
    const {
      categories: subCategories,
      find: findInDescendant,
    } = categoriesFormatter(
      category.children,
      slug,
      formattedCategory.path,
      category.slug === slug || findInAncestors,
    );
    if (category.slug === slug || findInAncestors) {
      formattedCategory.children = subCategories;
      formattedCategories.push(formattedCategory);
    } else if (findInDescendant) {
      formattedCategories = subCategories;
      find = true;
    }
  });

  return {
    categories: formattedCategories,
    find,
  };
};

const dataFormatter = (data, pageName) => {
  if (!data) return undefined;

  let formattedData = {};
  const products = {
    meta: {},
    data: [],
  };

  data.pages.forEach((pageData, i) => {
    const pageProductsData = [...pageData.products.data];
    if (pageProductsData.length > 0) {
      pageProductsData[0].firstItemPage = pageData.products.meta.current_page;
    }

    products.meta = pageData.products.meta;
    products.data.push(...pageProductsData);
    if (i === data.pages.length - 1) {
      formattedData = {
        ...pageData,
        products,
        sorts: {
          ...pageData.sorts,
        },
      };
    }
  });
  if (pageName === PAGE_PRODUCTS_CATEGORY) {
    formattedData.categories = categoriesFormatter(
      formattedData.categories,
      formattedData.slug,
      formattedData.path,
    ).categories;
  }

  return formattedData;
};

const ProductsContext = createContext(null);

const ProductsContextProvider = ({
  children,
  pageName,
  withPricesAndStocks,
}) => {
  const { isMateriaux } = useConfig();
  const {
    params,
    setParams,
    getParams,
    apiParams,
    hasSearchParamsDiff,
  } = usePageParams(paramsConfig, consistencyEncoder, apiParamsEncoder);
  const URLParams = useParams();
  const apiNodeEndpoint = getApiNodeEndpoint(pageName, URLParams, isMateriaux);
  const isFetchDisabled =
    apiNodeEndpoint === null || //Current page not displaying products
    hasSearchParamsDiff;

  const queryParams = { ...apiParams };
  if (pageName === PAGE_PRODUCTS_DISCOUNT && URLParams.discount) {
    queryParams.discount = URLParams.discount;
  }
  const queryKey = [apiNodeEndpoint, queryParams];

  const { total, totalWithPrice, totalWithStock } = usePricesAndStocks(
    queryKey,
    pagesProductsMapper,
    pagesProductsMutator,
    withPricesAndStocks,
    false,
  );
  const hasAllPrices = totalWithPrice === total;
  const hasAllStocks = totalWithStock === total;

  const {
    data,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    fetchPreviousPage,
    fetchNextPage,
    hasPreviousPage,
    hasNextPage,
  } = useProducts(queryKey, { ...params, pageName }, isFetchDisabled);
  const isAllProductsLoaded = getIsAllProductsLoaded(data);
  const { localSorts } = useLocalSorts(
    pageName,
    !hasAllPrices || !isAllProductsLoaded,
    !hasAllStocks || !isAllProductsLoaded,
  );
  const formattedData = useMemo(() => dataFormatter(data, pageName), [
    data,
    pageName,
  ]);
  const sortedData = useMemo(
    () => applyLocalSorts(formattedData, localSorts, params),
    [localSorts, formattedData, params],
  );

  const { data: discountClasses } = useDiscountClasses();

  return (
    <ProductsContext.Provider
      value={{
        getParams,
        setParams,
        data: sortedData,
        discountClasses,
        isFetching,
        isLoading,
        isError,
        isSuccess,
        fetchPreviousPage,
        fetchNextPage,
        hasPreviousPage,
        hasNextPage,
        hasPageChange:
          formattedData?.params?.pageName === undefined ||
          formattedData.params.pageName !== pageName,
        isDiscountPageChange:
          (formattedData?.params.pageName === PAGE_PRODUCTS_OFFERS &&
            pageName === PAGE_PRODUCTS_DISCOUNT) ||
          (formattedData?.params.pageName === PAGE_PRODUCTS_DISCOUNT &&
            pageName === PAGE_PRODUCTS_DISCOUNT),
      }}
    >
      {children}
    </ProductsContext.Provider>
  );
};

ProductsContextProvider.defaultProps = {
  ...ProductsContextProviderDefaultProps,
};

ProductsContextProvider.propTypes = {
  ...ProductsContextProviderPropTypes,
};

export const useProductsContext = () => useContext(ProductsContext);

export default ProductsContextProvider;
